// The website's online shop - talks to /public/shop (no sign-in) and keeps
// the cart, the customer's delivery details and their orders' private links
// on this phone, so it behaves like an app: open it again and everything is
// still there. Prices and totals are always worked out again on the server.
import { useSyncExternalStore } from 'react';
import apiClient from '../services/api';

export interface ShopProduct {
  id: string;
  name: string;
  category: string;
  pack_label: string;
  price: string;
  description: string | null;
  photo: string | null;
  in_stock: boolean;
  featured: boolean;
  max_per_order: number;
}

export interface ShopFront {
  open: boolean;
  paused_message: string;
  notice: string;
  payee_name: string;
  upi: boolean;
  cash_on_delivery: boolean;
  delivery_fee: string;
  free_delivery_above: string;
  min_order: string;
  pincodes: string[];
  delivery_area_note: string;
  slots: string[];
  dates: string[];
  support_phone: string;
  order_note: string;
  products: ShopProduct[];
}

export interface OrderItem { product_id: string | null; name: string; pack_label: string; unit_price: string; quantity: number; line_total: string }
export interface OrderEvent { label: string; note: string | null; by_customer: boolean; created_at: string }

export type OrderStatus = 'Placed' | 'Confirmed' | 'Out for delivery' | 'Delivered' | 'Cancelled';
export type PaymentStatus = 'Awaiting payment' | 'Paid - to verify' | 'Verified' | 'Not received' | 'Refunded' | 'Cash on delivery';

export interface PublicOrder {
  ref: string;
  status: OrderStatus;
  payment_status: PaymentStatus;
  payment_method: 'UPI' | 'Cash';
  customer_name: string;
  mobile_masked: string;
  address: string;
  delivery_date: string;
  delivery_slot: string;
  items: OrderItem[];
  subtotal: string;
  delivery_fee: string;
  total: string;
  utr: string | null;
  upi_id: string | null;
  payee_name: string | null;
  upi_link: string | null;
  can_pay: boolean;
  can_cancel: boolean;
  cancel_reason: string | null;
  created_at: string;
  timeline: OrderEvent[];
  token?: string | null;
}

export interface Details {
  customer_name: string;
  mobile: string;
  flat: string;
  building: string;
  area: string;
  landmark: string;
  pincode: string;
}

export const shopApi = {
  front: () => apiClient.get<ShopFront>('/public/shop').then((r) => r.data),
  place: (body: Record<string, unknown>) => apiClient.post<PublicOrder>('/public/shop/orders', body).then((r) => r.data),
  get: (ref: string, token: string) => apiClient.get<PublicOrder>(`/public/shop/orders/${ref}`, { params: { token } }).then((r) => r.data),
  lookup: (ref: string, mobile: string) => apiClient.post<PublicOrder>('/public/shop/orders/lookup', { ref, mobile }).then((r) => r.data),
  paid: (ref: string, token: string, utr: string) => apiClient.post<PublicOrder>(`/public/shop/orders/${ref}/paid`, { token, utr }).then((r) => r.data),
  cancel: (ref: string, token: string, reason?: string) => apiClient.post<PublicOrder>(`/public/shop/orders/${ref}/cancel`, { token, reason }).then((r) => r.data),
};

export function rupees(v: string | number): string {
  const n = Number(v);
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 })}`;
}

// ---------- on this phone ----------
function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}
function write(key: string, v: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(v));
  } catch {
    // private window - still works for this visit
  }
}

type Cart = Record<string, number>; // product id -> quantity
export interface SavedOrder { ref: string; token: string; total: string; placed: string }

const CART = 'skg.shop.cart';
const DETAILS = 'skg.shop.details';
const ORDERS = 'skg.shop.orders';

let cart: Cart = read<Cart>(CART, {});
let saved: SavedOrder[] = read<SavedOrder[]>(ORDERS, []);
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

export function useCart(): Cart {
  return useSyncExternalStore(subscribe, () => cart);
}
export function setQty(id: string, qty: number): void {
  const next = { ...cart };
  if (qty <= 0) delete next[id];
  else next[id] = qty;
  cart = next;
  write(CART, cart);
  emit();
}
export function clearCart(): void {
  cart = {};
  write(CART, cart);
  emit();
}
export function cartCount(c: Cart): number {
  return Object.values(c).reduce((a, b) => a + b, 0);
}

export function getDetails(): Details {
  return { customer_name: '', mobile: '', flat: '', building: '', area: '', landmark: '', pincode: '', ...read<Partial<Details>>(DETAILS, {}) };
}
export function saveDetails(d: Details): void {
  write(DETAILS, d);
}

export function useSavedOrders(): SavedOrder[] {
  return useSyncExternalStore(subscribe, () => saved);
}
export function rememberOrder(o: PublicOrder): void {
  if (!o.token) return;
  saved = [{ ref: o.ref, token: o.token, total: o.total, placed: o.created_at }, ...saved.filter((s) => s.ref !== o.ref)].slice(0, 30);
  write(ORDERS, saved);
  emit();
}
export function tokenFor(ref: string): string | null {
  return saved.find((s) => s.ref === ref)?.token ?? null;
}
export function forgetOrder(ref: string): void {
  saved = saved.filter((s) => s.ref !== ref);
  write(ORDERS, saved);
  emit();
}
